import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/axios';
import { socket, joinMediasoupRoom, mediasoupClient } from '../../api/socket';
import { useAuth } from '../../context/AuthContext';
import {
  Send, Clock, Plus, Video, ZoomIn, ZoomOut, Loader2, Scan, ListChecks,
  Wallet, BookOpen, X, MessageCircle, Volume2, Maximize2, Minimize2, Camera, LogOut, ChevronRight,
  Cpu, Wifi, WifiOff, Star, Sparkles, Home
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import DepositModal from '../../components/DepositModal';
import { useTranslation } from 'react-i18next';

interface ScanData {
  imageData: string;
}

declare global {
  interface Window {
    cv: any;
    cvReady: boolean;
  }
}

const StudentDashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  const [roomId, setRoomId] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [messages, setMessages] = useState<{ sender: string, text: string, type: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isLowBandwidthMode, setIsLowBandwidthMode] = useState(false);
  const [isDrawingAllowed, setIsDrawingAllowed] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [laserPos, setLaserPos] = useState({ x: 0, y: 0, active: false });
  const [activeQuiz, setActiveQuiz] = useState<any>(null);
  const [textAnswer, setTextAnswer] = useState('');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const deviceRef = useRef<any>(null);
  const recvTransportRef = useRef<any>(null);
  const sendTransportRef = useRef<any>(null);
  const videoProducerRef = useRef<any>(null);
  const isJoiningRef = useRef(false);
  const hasListeners = useRef(false);
  const lastImgRef = useRef<HTMLImageElement | null>(null);
  const lastProcessedMessageRef = useRef<string>('');
  const lastRemoteXRef = useRef<number | null>(null);
  const lastRemoteYRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const renderMapRef = useRef<((img: HTMLImageElement) => void) | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const [isEngineReady, setIsEngineReady] = useState(false);
  const [studentStreams, setStudentStreams] = useState<any[]>([]);

  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  const strokesRef = useRef<any[]>([]);
  const consumedProducersRef = useRef<Set<string>>(new Set());
  const isStartingCamRef = useRef(false);

  const roomIdRef = useRef(roomId);
  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  // Clean up WebRTC Transports on Unmount
  useEffect(() => {
    return () => {
      if (videoProducerRef.current) videoProducerRef.current.close();
      if (sendTransportRef.current) sendTransportRef.current.close();
      if (recvTransportRef.current) recvTransportRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (!socket || !roomId) return;

    if (isLowBandwidthMode) {
      if (videoProducerRef.current && !videoProducerRef.current.paused) {
        videoProducerRef.current.pause();
        socket.emit('pause-producer', { roomId, producerId: videoProducerRef.current.id });
      }
      socket.emit('apply-bandwidth-limit', { roomId, mode: 'low' });
    } else {
      if (videoProducerRef.current && videoProducerRef.current.paused && isLive) {
        videoProducerRef.current.resume();
        socket.emit('resume-producer', { roomId, producerId: videoProducerRef.current.id });
      }
      socket.emit('apply-bandwidth-limit', { roomId, mode: 'high' });
    }
  }, [isLowBandwidthMode, roomId, isLive]);

  const consumeStream = useCallback(async (
    producerId: string,
    kind: string,
    manualRoomId?: string,
    appData?: any,
    producerSocketId?: string
  ) => {
    if (consumedProducersRef.current.has(producerId)) return;
    consumedProducersRef.current.add(producerId);

    const activeRoomId = manualRoomId || roomId;

    if (!deviceRef.current || !recvTransportRef.current) {
      consumedProducersRef.current.delete(producerId);
      setTimeout(() => consumeStream(producerId, kind, activeRoomId, appData, producerSocketId), 500);
      return;
    }

    const rtpCapabilities = deviceRef.current.rtpCapabilities;
    if (!rtpCapabilities) {
      consumedProducersRef.current.delete(producerId);
      return;
    }

    socket.emit('consume', {
      roomId: activeRoomId,
      transportId: recvTransportRef.current.id,
      rtpCapabilities,
      producerId,
    }, async (params: any) => {
      if (params.error) {
        consumedProducersRef.current.delete(producerId);
        return;
      }

      try {
        const consumer = await recvTransportRef.current!.consume(params);
        const stream = new MediaStream([consumer.track]);

        if (kind === 'audio') {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = stream;
            remoteAudioRef.current.play().catch(e => console.warn("🔊 Audio blocked", e));
          }
        }
        else if (kind === 'video') {
          const rawUserId = appData?.userId || params.appData?.userId || producerSocketId;
          const participantUserId = String(rawUserId || "unknown").trim();

          if (typeof setStudentStreams !== 'undefined') {
            setStudentStreams((prev: any[]) => {
              const cleanList = prev.filter(s => {
                const existingId = String(s.userId || "").trim();
                const existingSocket = String(s.id || "").trim();
                return !(existingId === participantUserId || existingSocket === producerSocketId || existingId === "undefined");
              });
              return [
                ...cleanList,
                { id: producerSocketId, userId: participantUserId, stream, kind: 'video' }
              ];
            });
          }

          const attachToUi = (attempts = 0) => {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = stream;
              // Muted to ensure autoplay works without interaction
              remoteVideoRef.current.play().catch(err => console.error("🎥 Play error:", err));
            } else if (attempts < 5) {
              setTimeout(() => attachToUi(attempts + 1), 1000);
            }
          };
          attachToUi();

          if (isLowBandwidthMode) {
            socket.emit('pause-consumer', { roomId: activeRoomId, consumerId: consumer.id });
          }
        }

        socket.emit('resume-consumer', {
          roomId: activeRoomId,
          consumerId: consumer.id
        });

      } catch (err) {
        consumedProducersRef.current.delete(producerId);
      }
    });
  }, [roomId, socket, isLowBandwidthMode]);

  const handleJoin = useCallback(async (manualId?: string) => {
    const targetRoomId = manualId || roomId;
    if (!targetRoomId) return;

    if (isJoiningRef.current) return;
    isJoiningRef.current = true;
    setRoomId(targetRoomId);

    try {
      const currentUserId = user?.id || (user as any)?.uid || '';

      const [response, transportParams] = await Promise.all([
        joinMediasoupRoom(targetRoomId, 'STUDENT', currentUserId),
        new Promise<any>((resolve) => {
          socket.emit('create-transport', {
            roomId: targetRoomId,
            direction: 'recv'
          }, (res: any) => resolve(res));
        })
      ]);

      if (transportParams.error) throw new Error(`Transport creation failed: ${transportParams.error}`);

      if (!deviceRef.current) {
        try {
          const newDevice = new mediasoupClient.Device();
          await newDevice.load({ routerRtpCapabilities: response.routerRtpCapabilities });
          deviceRef.current = newDevice;
          setIsEngineReady(true);
        } catch (deviceErr) {
          isJoiningRef.current = false;
          return;
        }
      }

      const transport = deviceRef.current!.createRecvTransport({
        ...transportParams,
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:atrons.com.et:3478' },
          {
            urls: 'turns:atrons.com.et:5349?transport=tcp', 
            username: 'atrons',
            credential: 'Myatrons@com1'
          }
        ],
      });

      transport.on('connect', ({ dtlsParameters }: any, callback: any, errback: any) => {
        socket.emit('transport-connect', {
          transportId: transport.id,
          dtlsParameters,
          roomId: targetRoomId
        }, (res: any) => {
          if (res?.error) errback(res.error);
          else callback();
        });
      });

      recvTransportRef.current = transport;
      setIsConnected(true);
      isJoiningRef.current = false;

      socket.emit('join-whiteboard', { roomId: targetRoomId, page: 1 });

      if (response.existingProducers && response.existingProducers.length > 0) {
        setTimeout(() => {
          response.existingProducers.forEach((p: any) => {
            if (p.socketId === socket.id) return;
            consumeStream(p.producerId, p.kind, targetRoomId, p.appData, p.socketId);
          });
        }, 400);
      }

    } catch (err: any) {
      isJoiningRef.current = false;
      setTimeout(() => { isJoiningRef.current = false; }, 3000);
    }
  }, [roomId, user?.id, (user as any)?.uid, consumeStream, socket]);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);

    if (canvas.style.width === `${width}px` && canvas.style.height === `${height}px`) return;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    if (lastImgRef.current) {
      const img = lastImgRef.current;
      const ratio = Math.min(width / img.width, height / img.height);
      const offsetX = (width - img.width * ratio) / 2;
      const offsetY = (height - img.height * ratio) / 2;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, offsetX, offsetY, img.width * ratio, img.height * ratio);
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let rx: number | null = null;
    let ry: number | null = null;

    strokesRef.current.forEach(pt => {
      const px = pt.x * (canvas.width / dpr);
      const py = pt.y * (canvas.height / dpr);
      ctx.globalAlpha = pt.alpha || 1;
      ctx.strokeStyle = pt.color;
      ctx.lineWidth = pt.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (!pt.isDragging) {
        ctx.beginPath();
        ctx.moveTo(px, py);
        rx = px; ry = py;
      } else {
        if (rx !== null && ry !== null) {
          const midX = (rx + px) / 2;
          const midY = (ry + py) / 2;

          ctx.quadraticCurveTo(rx, ry, midX, midY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(midX, midY);
        } else {
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(px, py);
          ctx.stroke();
        }
        rx = px; ry = py;
      }
    });

  }, []);

  useEffect(() => {
    if (!isConnected || !socket || !socket.connected) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const renderMap = (img: HTMLImageElement) => {
      if (!ctx || !canvas) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const logicalWidth = canvas.width / dpr;
      const logicalHeight = canvas.height / dpr;
      const ratio = Math.min(logicalWidth / img.width, logicalHeight / img.height);
      const offsetX = (logicalWidth - img.width * ratio) / 2;
      const offsetY = (logicalHeight - img.height * ratio) / 2;
      ctx.scale(dpr, dpr);
      ctx.drawImage(img, offsetX, offsetY, img.width * ratio, img.height * ratio);

      let rx: number | null = null, ry: number | null = null;
      strokesRef.current.forEach(pt => {
        const px = pt.x * logicalWidth;
        const py = pt.y * logicalHeight;
        ctx.globalAlpha = pt.alpha || 1;
        ctx.strokeStyle = pt.color;
        ctx.lineWidth = pt.width;
        if (!pt.isDragging) {
          ctx.beginPath(); ctx.moveTo(px, py); rx = px; ry = py;
        } else if (rx !== null && ry !== null) {
          ctx.beginPath(); ctx.moveTo(rx, ry); ctx.quadraticCurveTo((rx + px) / 2, (ry + py) / 2, px, py); ctx.stroke();
          rx = px; ry = py;
        }
      });
    };

    const onDraw = ({ page, pathData }: any) => {
      if (page !== currentPage) return;

      if (!ctx) return;
      strokesRef.current.push(pathData);

      const x = pathData.x * (canvas.width / dpr);
      const y = pathData.y * (canvas.height / dpr);
      ctx.globalAlpha = pathData.alpha || 1;
      ctx.strokeStyle = pathData.color;
      ctx.lineWidth = pathData.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (!pathData.isDragging) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        lastRemoteXRef.current = x;
        lastRemoteYRef.current = y;
      } else {
        if (lastRemoteXRef.current !== null && lastRemoteYRef.current !== null) {
          const prevX = lastRemoteXRef.current;
          const prevY = lastRemoteYRef.current;
          const midX = (prevX + x) / 2;
          const midY = (prevY + y) / 2;

          ctx.quadraticCurveTo(prevX, prevY, midX, midY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(midX, midY);
        } else {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        lastRemoteXRef.current = x;
        lastRemoteYRef.current = y;
      }
    };

    const onSyncCanvasFull = ({ page, image }: any) => {
      if (page !== currentPage) return;

      const img = new Image();
      img.onload = () => {
        lastImgRef.current = img;
        renderMap(img);
      };
      img.src = image;
    };

    const handleTouch = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();

      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();

      const mouseType = e.type === 'touchstart' ? 'mousedown' : 'mousemove';
      const mouseEvent = new MouseEvent(mouseType, {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      canvas.dispatchEvent(mouseEvent);
    };

    let isDrawing = false;
    const studentColor = '#10b981';
    const studentWidth = 3;

    const startDrawing = (e: MouseEvent) => {
      if (!isDrawingAllowed) return;
      isDrawing = true;
      const rect = canvas.getBoundingClientRect();
      const pathData = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        color: studentColor,
        width: studentWidth,
        isDragging: false
      };
      onDraw({ page: currentPage, pathData });
      socket.emit('draw-data', { roomId, page: currentPage, pathData });
    };

    const draw = (e: MouseEvent) => {
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const pathData = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        color: studentColor,
        width: studentWidth,
        isDragging: true
      };
      onDraw({ page: currentPage, pathData });
      socket.emit('draw-data', { roomId, page: currentPage, pathData });
    };

    const stopDrawing = () => {
      isDrawing = false;
    };

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);

    setupCanvas();
    window.addEventListener('resize', setupCanvas);
    window.addEventListener('orientationchange', setupCanvas);

    canvas.addEventListener('touchstart', handleTouch, { passive: false });
    canvas.addEventListener('touchmove', handleTouch, { passive: false });
    canvas.addEventListener('touchend', () => {
      const mouseEvent = new MouseEvent('mouseup');
      canvas.dispatchEvent(mouseEvent);
    }, { passive: false });

    const onNewProducer = (d: { producerId: string; kind: string; socketId: string; appData?: any }) => {
      consumeStream(d.producerId, d.kind, roomId, d.appData, d.socketId);
    };

    const onWhiteboardHistory = (history: any[]) => {
      strokesRef.current = history || [];
      setupCanvas();
    };

    const onPageChanged = ({ page, history, background }: any) => {
      setCurrentPage(page);
      if (page > totalPages) setTotalPages(page);

      strokesRef.current = history || [];

      if (background) {
        const img = new Image();
        img.onload = () => {
          lastImgRef.current = img;
          setupCanvas();
        };
        img.src = background;
      } else {
        lastImgRef.current = null;
        setupCanvas();
      }
    };

    socket.on('draw-data', onDraw);
    socket.on('sync-background', onSyncCanvasFull);
    socket.on('whiteboard-history', onWhiteboardHistory);
    socket.on('page-changed', onPageChanged);

    socket.on('clear-whiteboard', () => {
      lastImgRef.current = null;
      strokesRef.current = [];
      ctx?.setTransform(1, 0, 0, 1, 0, 0);
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      ctx?.scale(dpr, dpr);
      lastRemoteXRef.current = null;
      lastRemoteYRef.current = null;
    });

    socket.on('laser-move', setLaserPos);
    socket.on('new-producer', onNewProducer);

    return () => {
      window.removeEventListener('resize', setupCanvas);
      window.removeEventListener('orientationchange', setupCanvas);
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('touchmove', handleTouch);
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseout', stopDrawing);

      socket.off('draw-data', onDraw);
      socket.off('sync-background', onSyncCanvasFull);
      socket.off('whiteboard-history', onWhiteboardHistory);
      socket.off('page-changed', onPageChanged);
      socket.off('clear-whiteboard');
      socket.off('laser-move');
      socket.off('new-producer', onNewProducer);
    };
  }, [isConnected, socket, setupCanvas, roomId, consumeStream, isDrawingAllowed, currentPage, totalPages]);

  useEffect(() => {
    if (!socket) return;

    const onSessionEnded = (data: any) => {
      console.log("Session ended by tutor", data);
      setIsLive(false);

      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      if (localVideoRef.current) {
        const stream = localVideoRef.current.srcObject as MediaStream;
        stream?.getTracks().forEach(track => track.stop());
        localVideoRef.current.srcObject = null;
      }

      setShowRatingModal(true);
    };

    socket.on('session-ended', onSessionEnded);

    return () => {
      socket.off('session-ended', onSessionEnded);
    };
  }, [socket]);

  const transformPerspective = (src: any, contour: any) => {
    const cv = window.cv;
    const points: { x: number, y: number }[] = [];

    for (let i = 0; i < contour.rows; i++) {
      points.push({
        x: contour.data32S[i * 2],
        y: contour.data32S[i * 2 + 1]
      });
    }

    const sortedByY = [...points].sort((a, b) => a.y - b.y);
    const topList = sortedByY.slice(0, 2).sort((a, b) => a.x - b.x);
    const bottomList = sortedByY.slice(2, 4).sort((a, b) => a.x - b.x);

    const tl = topList[0];
    const tr = topList[1];
    const br = bottomList[1];
    const bl = bottomList[0];

    const width = 800;
    const height = 1100;

    const srcCoords = cv.matFromArray(4, 1, cv.CV_32FC2, [
      tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y
    ]);

    const dstCoords = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0, 0, width, 0, width, height, 0, height
    ]);

    const warped = new cv.Mat();
    const M = cv.getPerspectiveTransform(srcCoords, dstCoords);

    try {
      cv.warpPerspective(src, warped, M, new cv.Size(width, height), cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
    } catch (err) { }
    finally {
      M.delete();
      srcCoords.delete();
      dstCoords.delete();
    }

    return warped;
  };

  const handleSmartScan = useCallback(async () => {
    const cv = window.cv;
    const currentRoomId = roomIdRef.current;
    const video = localVideoRef.current;

    if (!cv || !cv.imread || !video || video.readyState < 4) return;
    if (video.videoWidth === 0 || video.videoHeight === 0) return;
    if (!currentRoomId) return;

    setIsScanning(true);
    setOcrProgress(10);
    await new Promise(resolve => setTimeout(resolve, 100));

    let src = null;
    let dst = null;
    let contours = null;
    let hierarchy = null;
    let paperContour = null;

    try {
      const MAX_DIM = 800;
      const scale = Math.min(MAX_DIM / video.videoWidth, MAX_DIM / video.videoHeight, 1);
      const scaledWidth = video.videoWidth * scale;
      const scaledHeight = video.videoHeight * scale;

      const proxyCanvas = document.createElement('canvas');
      proxyCanvas.width = scaledWidth;
      proxyCanvas.height = scaledHeight;
      const ctx = proxyCanvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error("Could not create canvas context");

      ctx.drawImage(video, 0, 0, scaledWidth, scaledHeight);
      src = cv.imread(proxyCanvas);
      dst = new cv.Mat();

      cv.cvtColor(src, dst, cv.COLOR_RGBA2GRAY);

      let blurred = new cv.Mat();
      cv.bilateralFilter(dst, blurred, 9, 75, 75, cv.BORDER_DEFAULT);

      let thresholded = new cv.Mat();
      cv.adaptiveThreshold(blurred, thresholded, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 11, 2);

      contours = new cv.MatVector();
      hierarchy = new cv.Mat();
      cv.findContours(thresholded, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

      let maxArea = 0;
      for (let i = 0; i < contours.size(); ++i) {
        let cnt = contours.get(i);
        let area = cv.contourArea(cnt);

        if (area > (scaledWidth * scaledHeight * 0.15)) {
          let approx = new cv.Mat();
          cv.approxPolyDP(cnt, approx, 0.02 * cv.arcLength(cnt, true), true);

          if (approx.rows === 4 && area > maxArea) {
            if (paperContour) paperContour.delete();
            paperContour = approx;
            maxArea = area;
          } else {
            approx.delete();
          }
        }
        cnt.delete();
      }

      blurred.delete();
      thresholded.delete();

      const syncCanvas = document.createElement('canvas');
      let finalImgData = null;

      if (paperContour) {
        setOcrProgress(50);
        let warped = transformPerspective(src, paperContour);

        let enhanced = new cv.Mat();
        warped.convertTo(enhanced, -1, 1.4, -15);

        let sharpened = new cv.Mat();
        let kernelData = [0, -1, 0, -1, 5, -1, 0, -1, 0];
        let kernel = cv.matFromArray(3, 3, cv.CV_32F, kernelData);
        cv.filter2D(enhanced, sharpened, -1, kernel);

        syncCanvas.width = sharpened.cols;
        syncCanvas.height = sharpened.rows;
        cv.imshow(syncCanvas, sharpened);

        finalImgData = syncCanvas.toDataURL('image/jpeg', 0.85);

        enhanced.delete();
        sharpened.delete();
        kernel.delete();
        warped.delete();
      } else {
        syncCanvas.width = scaledWidth;
        syncCanvas.height = scaledHeight;
        cv.imshow(syncCanvas, src);
        finalImgData = syncCanvas.toDataURL('image/jpeg', 0.6);
      }

      if (finalImgData && socket.connected) {
        const img = new Image();
        img.onload = () => {
          lastImgRef.current = img;
          if (renderMapRef.current) renderMapRef.current(img);
        };
        img.src = finalImgData;

        socket.emit('sync-background', {
          roomId: currentRoomId,
          page: currentPage,
          image: finalImgData
        });
      }

      setOcrProgress(100);

    } catch (err) {
      console.error("❌ [SCANNER] Error:", err);
    } finally {
      if (src) src.delete();
      if (dst) dst.delete();
      if (contours) contours.delete();
      if (hierarchy) hierarchy.delete();
      if (paperContour) paperContour.delete();

      setTimeout(() => {
        setIsScanning(false);
        setOcrProgress(0);
      }, 500);
    }
  }, [socket, roomId, transformPerspective, currentPage]);

  useEffect(() => {
    if (!isConnected || !socket) return;

    const onRemoteScanRequest = () => {
      handleSmartScan();
    };

    const onBroadcastQuiz = (data: any) => {
      setActiveQuiz(data);
    };

    const onToggleDrawing = (allowed: boolean) => {
      setIsDrawingAllowed(allowed);
    };

    socket.off('remote-scan-request');
    socket.off('broadcast-quiz');
    socket.off('toggle-student-drawing');

    socket.on('remote-scan-request', onRemoteScanRequest);
    socket.on('broadcast-quiz', onBroadcastQuiz);
    socket.on('toggle-student-drawing', onToggleDrawing);

    return () => {
      socket.off('remote-scan-request', onRemoteScanRequest);
      socket.off('broadcast-quiz', onBroadcastQuiz);
      socket.off('toggle-student-drawing', onToggleDrawing);
    };
  }, [isConnected, socket, handleSmartScan]);

  useEffect(() => {
    if (!socket) return;

    const handleChat = (data: { sender: string; text: string; timestamp?: number }) => {
      const messageFingerprint = `${data.sender}:${data.text}`;

      if (lastProcessedMessageRef.current === messageFingerprint) return;

      const isMe = data.sender.trim().toLowerCase() === user?.fullName?.trim().toLowerCase();
      if (isMe) return;

      lastProcessedMessageRef.current = messageFingerprint;

      setMessages(prev => {
        const isDuplicate = prev.length > 0 &&
          prev[prev.length - 1].text === data.text &&
          prev[prev.length - 1].sender === data.sender;

        if (isDuplicate) return prev;
        return [...prev, { ...data, type: 'other' }];
      });
    };

    socket.off('receive-chat', handleChat);
    socket.on('receive-chat', handleChat);

    return () => {
      socket.off('receive-chat', handleChat);
    };
  }, [socket, user?.fullName]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let r = params.get('room');

    if (!r) {
      const lastPart = window.location.pathname.split('/').pop();
      if (lastPart && lastPart.length > 30) r = lastPart;
    }

    if (!r || isConnected || isJoiningRef.current) return;

    const attemptJoin = () => {
      if (socket && socket.connected) {
        handleJoin(r);
      }
    };
    attemptJoin();

    if (socket) {
      socket.on('connect', attemptJoin);
      return () => { socket.off('connect', attemptJoin); };
    }
  }, [socket, isConnected, handleJoin]);

  const sendChat = () => {
    if (!chatInput.trim()) return;
    socket.emit('send-chat', { roomId, text: chatInput, sender: user?.fullName || 'Student' });
    setMessages(p => [...p, { sender: 'Me', text: chatInput, type: 'me' }]);
    setChatInput('');
  };

  useEffect(() => {
    const checkCV = setInterval(() => {
      if (window.cv && window.cv.imread) {
        window.cvReady = true;
        clearInterval(checkCV);
      }
    }, 1000);
    return () => clearInterval(checkCV);
  }, []);

  const startMyCamera = async () => {
    if (!deviceRef.current || !socket?.connected) {
      setTimeout(startMyCamera, 500);
      return;
    }

    if (isStartingCamRef.current) return;
    isStartingCamRef.current = true; 

    const currentUserId = user?.id || (user as any)?.uid;
    if (!currentUserId) {
      isStartingCamRef.current = false;
      return;
    }

    try {
      if (localVideoRef.current && localVideoRef.current.srcObject) {
        const oldStream = localVideoRef.current.srcObject as MediaStream;
        oldStream.getTracks().forEach(track => track.stop());
        localVideoRef.current.srcObject = null;
      }

      if (videoProducerRef.current && !videoProducerRef.current.closed) {
        try { videoProducerRef.current.close(); } catch (e) { }
        videoProducerRef.current = null;
      }

      if (sendTransportRef.current && !sendTransportRef.current.closed) {
        try { sendTransportRef.current.close(); } catch (e) { }
        sendTransportRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000
        }
      });

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      socket.emit('create-transport', { roomId, direction: 'send' }, async (p: any) => {
        if (p.error || !p.id) {
          isStartingCamRef.current = false;
          return;
        }

        try {
          const transport = deviceRef.current.createSendTransport({
            ...p,
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' },
              { urls: 'stun:atrons.com.et:3478' },
              {
                urls: 'turns:atrons.com.et:5349?transport=tcp', 
                username: 'atrons',
                credential: 'Myatrons@com1'
              }
            ],
          });

          transport.on('connect', ({ dtlsParameters }: any, cb: any, err: any) => {
            socket.emit('transport-connect', { transportId: transport.id, dtlsParameters, roomId },
              (res: any) => res?.error ? err(res.error) : cb());
          });

          transport.on('produce', ({ kind, rtpParameters, appData }: any, cb: any, err: any) => {
            socket.emit('transport-produce', {
              transportId: transport.id,
              kind,
              rtpParameters,
              roomId,
              appData: { ...appData, userId: currentUserId, kind }
            }, (res: any) => {
              if (res?.error) return err(res.error);
              cb({ id: res.id });
            });
          });

          sendTransportRef.current = transport;

          const tracks = stream.getTracks();
          for (const track of tracks) {
            const producer = await transport.produce({
              track,
              appData: { kind: track.kind, userId: currentUserId },
              encodings: track.kind === 'video'
                ? [{ maxBitrate: 600000, priority: 'high' }]
                : [{ maxBitrate: 64000, priority: 'high' }],
              codecOptions: track.kind === 'audio' ? { opusDtx: true, opusFec: true } : undefined
            });

            if (track.kind === 'video') {
              videoProducerRef.current = producer;
            }
          }

          setIsLive(true);
          isStartingCamRef.current = false; 
        } catch (queueErr) {
          console.warn("⚠️ [MEDIASOUP] Transport produce queue error caught:", queueErr);
          isStartingCamRef.current = false;
        }
      });
    } catch (err) {
      console.error("Camera access failed:", err);
      isStartingCamRef.current = false;
    }
  };

  const handleSubmitAnswer = (answer: string) => {
    socket.emit('quiz-response', { roomId, answer, studentName: user?.fullName });
    setMessages(p => [...p, { sender: 'Me', text: `📝 Answered: ${answer}`, type: 'me' }]);
    setActiveQuiz(null);
  };

  const submitRating = async () => {
    if (rating === 0) return;
    setIsSubmittingRating(true);
    try {
      await api.post('/sessions/review', { sessionId: roomId, rating, comment: reviewComment }); setShowRatingModal(false);
      navigate('/student/home');
    } catch (err) {
      console.error("Failed to submit rating", err);
    } finally {
      setIsSubmittingRating(false);
    }
  };

  return (
    <div className="h-[100dvh] w-full flex flex-col bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 overflow-hidden relative">
      <header className="h-16 md:h-20 shrink-0 flex items-center justify-between px-3 md:px-8 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl z-50 sticky top-0">
        <div className="flex items-center gap-2 md:gap-10 overflow-hidden">
          <div onClick={() => navigate('/')} className="flex items-center gap-2 cursor-pointer shrink-0">
            <div className="w-8 h-8 md:w-12 md:h-12 bg-indigo-600 rounded-xl md:rounded-2xl flex items-center justify-center font-black italic text-lg md:text-2xl text-white shadow-lg shadow-indigo-600/20 group-active:scale-90 transition-all">
              A
            </div>
            <div className="hidden xs:block">
              <h1 className="text-[10px] md:text-sm font-black uppercase tracking-tighter text-white">{t('app_name', 'AtronsTutor')}</h1>
              <p className="text-[7px] md:text-[9px] text-indigo-500 font-bold uppercase tracking-widest leading-none">Console</p>
            </div>
          </div>

          <nav className="flex gap-1 overflow-x-auto no-scrollbar py-1">
            <Link to="/student/dashboard" className="whitespace-nowrap px-3 py-1.5 rounded-lg text-[9px] font-black uppercase text-white bg-indigo-600 shadow-lg shadow-indigo-600/20">
              {t('dashboard', 'Dash')}
            </Link>
            <Link to="/student/my-jobs" className="whitespace-nowrap px-3 py-1.5 rounded-lg text-[9px] font-black uppercase text-slate-400 bg-slate-800 hover:text-white transition-all">
              {t('classes_nav', 'My Class')}
            </Link>
            <Link to="/student/post-job" className="whitespace-nowrap px-3 py-1.5 rounded-lg text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/20">
              + {t('new_request', 'New Request')}
            </Link>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setIsLowBandwidthMode(!isLowBandwidthMode)} className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase transition-all border ${isLowBandwidthMode ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'}`}>
            {isLowBandwidthMode ? <WifiOff size={14} /> : <Wifi size={14} />}
            <span className="hidden sm:inline">{isLowBandwidthMode ? t('data_saver_on', "Data Saver") : t('optimize_network', "Optimize")}</span>
          </button>

          <button onClick={() => setIsDepositOpen(true)} className="flex items-center gap-2 bg-slate-900 px-3 py-1.5 rounded-xl border border-slate-800 hover:border-indigo-500/50 transition-all shadow-xl">
            <Wallet size={14} className="text-indigo-400" />
            <div className="flex flex-col items-start leading-none">
              <span className="text-[10px] font-black text-white">{Number(user?.walletBalance || 0).toFixed(0)}</span>
              <span className="text-[6px] font-bold text-slate-500 uppercase">ETB</span>
            </div>
          </button>

          <button onClick={() => window.confirm(t('confirm_logout', "Sign out?")) && logout()} className="p-2.5 md:p-3 text-slate-500 bg-slate-800 border border-slate-700 rounded-xl md:rounded-2xl hover:text-red-500 hover:bg-red-500/10 transition-all flex items-center justify-center shadow-lg">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto lg:overflow-hidden bg-transparent custom-scrollbar overscroll-contain">
        <div className="flex flex-col lg:flex-row p-2 md:p-4 gap-3 md:gap-4 h-full relative">

          <section ref={containerRef} className="w-full min-h-[50vh] lg:h-full lg:flex-1 bg-white rounded-2xl md:rounded-[2.5rem] relative overflow-hidden border-2 lg:border-4 border-slate-800 shadow-2xl z-0 shrink-0">
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center bg-white/95 backdrop-blur px-4 py-2 rounded-2xl border shadow-xl">
              <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest text-center">
                {t('pg', 'Pg')} {currentPage} / {totalPages}
              </span>
            </div>

            <canvas ref={canvasRef} className="w-full h-full bg-white touch-none" />
            {laserPos.active && (
              <div className="absolute w-5 h-5 bg-red-500 rounded-full shadow-[0_0_15px_red] animate-ping pointer-events-none z-50" style={{ left: `${laserPos.x * 100}%`, top: `${laserPos.y * 100}%`, transform: 'translate(-50%, -50%)' }} />
            )}
          </section>

          <aside className="w-full lg:w-[360px] flex flex-col gap-3 h-auto lg:h-full pb-10 lg:pb-0 z-20 shrink-0 lg:overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 md:gap-3 shrink-0">
              <div className="bg-slate-950 rounded-xl md:rounded-3xl aspect-video overflow-hidden border-2 border-slate-700 relative shadow-lg">
                {/* Note: Video is muted to bypass browser autoplay policies */}
                <video ref={remoteVideoRef} autoPlay muted playsInline className="w-full h-full object-cover bg-black" />
                <div className="absolute top-2 left-2 bg-indigo-600/90 px-2 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest text-white flex items-center gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-white animate-pulse" />
                  {t('instructor', 'Instructor')}
                </div>
              </div>

              <div className="bg-slate-900 rounded-xl md:rounded-3xl min-h-[180px] aspect-video relative overflow-hidden border-2 border-slate-700 shadow-2xl group">
                <video
                  ref={localVideoRef}
                  autoPlay muted playsInline webkit-playsinline="true"
                  className={`w-full h-full object-cover transition-opacity duration-500 ${isLive ? 'opacity-100' : 'opacity-0'} ${isLowBandwidthMode ? 'brightness-50 blur-sm pointer-events-none' : ''}`}
                />

                {isLowBandwidthMode && isLive && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-md flex flex-col items-center justify-center p-4 text-center z-40">
                    <span className="text-[9px] font-black tracking-wider text-amber-400 uppercase bg-slate-900/90 border border-amber-500/30 px-3 py-1.5 rounded-xl">{t('local_camera_frozen', 'Local Camera Frozen')}</span>
                  </div>
                )}

                {isScanning && (
                  <div className="absolute inset-0 bg-indigo-900/90 backdrop-blur-xl flex flex-col items-center justify-center z-[100] p-4 text-center">
                    <Loader2 size={24} className="text-white animate-spin mb-2" />
                    <p className="text-[9px] font-black uppercase tracking-widest text-white mb-2">{t('analyzing_homework', 'Analyzing Homework')}</p>
                    <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-400 transition-all duration-500" style={{ width: `${ocrProgress}%` }} />
                    </div>
                  </div>
                )}

                {!isLive && (
                  <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-xl flex flex-col items-center justify-center p-6 text-center z-30 overflow-hidden">
                    {!isEngineReady ? (
                      <div className="flex flex-col items-center relative z-10">
                        <div className="relative mb-4"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>
                        <h3 className="text-[10px] font-black uppercase text-white tracking-[0.3em] mb-1">{t('initializing_engine', 'Initializing Engine')}</h3>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center relative z-10 animate-in fade-in zoom-in duration-500">
                        <div className="w-16 h-16 bg-indigo-500/10 rounded-full flex items-center justify-center mb-4 border border-indigo-500/20"><Video className="text-indigo-400 animate-bounce" size={24} /></div>

                        <button
                          disabled={isJoiningRef.current}
                          onClick={() => {
                            if (isJoiningRef.current) return;
                            if (remoteAudioRef.current) remoteAudioRef.current.play().catch(() => { });
                            try {
                              handleJoin(); 
                              startMyCamera(); 
                            } catch (err) { }
                          }}
                          className={`group relative px-10 py-3.5 rounded-2xl bg-indigo-600 text-white shadow-2xl active:scale-95 transition-all ${isJoiningRef.current ? 'opacity-50 cursor-wait' : 'opacity-100'}`}
                        >
                          <span className="relative text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2">
                            {isJoiningRef.current ? <><Loader2 size={14} className="animate-spin" /> {t('connecting', 'Connecting...')}</> : <>{t('enter_classroom', 'Enter Classroom')} <ChevronRight size={14} /></>}
                          </span>
                        </button>

                      </div>
                    )}
                  </div>
                )}

                {isLive && (
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-2 py-1 rounded-md border border-white/10">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-[8px] font-black uppercase tracking-widest text-white">{t('live', 'Live')}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="h-[300px] xs:h-[350px] lg:flex-1 bg-slate-800/80 backdrop-blur-md border border-slate-700 rounded-2xl md:rounded-3xl flex flex-col overflow-hidden shadow-2xl min-h-[300px]">
              <div className="p-3 border-b border-slate-700 text-[9px] font-black text-indigo-400 uppercase bg-slate-900/30 tracking-widest flex items-center gap-2">
                <MessageCircle size={14} /> {t('class_chat', 'Class Chat')}
              </div>
              <div className="flex-1 p-3 overflow-y-auto flex flex-col gap-2 text-[11px] custom-scrollbar bg-slate-950/20">
                {messages.map((m, i) => (
                  <div key={i} className={`flex flex-col ${m.type === 'me' ? 'items-end' : 'items-start'}`}>
                    <span className="text-[6px] text-slate-500 font-bold uppercase mb-0.5">{m.sender}</span>
                    <div className={`px-3 py-1.5 rounded-xl max-w-[85%] shadow-sm ${m.type === 'me' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-slate-700 text-slate-200 rounded-tl-none'}`}>{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="p-2 bg-slate-900/80 flex gap-2 border-t border-slate-700 m-1 rounded-xl">
                <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendChat()} placeholder={t('type_message', 'Type message...')} className="flex-1 bg-transparent px-2 text-[10px] outline-none text-white placeholder:text-slate-600" />
                <button onClick={sendChat} className="p-2 bg-indigo-600 rounded-lg text-white active:scale-90 transition-all"><Send size={14} /></button>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {activeQuiz && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-md flex items-center justify-center z-[110] p-4 text-slate-900">
          <div className="bg-white p-6 md:p-10 rounded-[2rem] w-full max-w-md shadow-2xl relative border-4 border-slate-800">
            <button onClick={() => setActiveQuiz(null)} className="absolute top-4 right-4 text-slate-300 hover:text-slate-800"><X size={20} /></button>
            <div className="flex items-center gap-3 mb-4 text-indigo-600"><BookOpen size={24} /><h3 className="text-xl font-black italic uppercase tracking-tighter">{t('quiz_time', 'Quiz Time')}</h3></div>
            <p className="text-lg font-bold mb-6 leading-tight text-slate-800">{activeQuiz.question}</p>
            {activeQuiz.type === 'mcq' ? (
              <div className="grid gap-2">
                {activeQuiz.options?.map((opt: string, i: number) => (
                  <button key={i} onClick={() => handleSubmitAnswer(opt)} className="w-full p-4 bg-slate-50 hover:bg-indigo-600 hover:text-white border-2 border-slate-100 rounded-xl text-left font-black transition-all text-[11px] uppercase italic">{opt}</button>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <textarea value={textAnswer} onChange={(e) => setTextAnswer(e.target.value)} className="w-full bg-slate-50 border-2 p-4 rounded-xl min-h-[100px] outline-none font-bold text-sm resize-none focus:border-indigo-600 transition-all" placeholder={t('your_answer', 'Your answer...')} />
                <button onClick={() => handleSubmitAnswer(textAnswer)} className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all">{t('submit_answer', 'Submit Answer')}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showRatingModal && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-md flex items-center justify-center z-[110] p-4 text-slate-900">
          <div className="bg-white p-6 md:p-10 rounded-[2rem] w-full max-w-md shadow-2xl border-4 border-indigo-500 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mb-4">
              <Sparkles size={32} />
            </div>
            <h3 className="text-2xl font-black italic uppercase tracking-tighter text-slate-900 mb-2">{t('class_complete', 'Class Complete!')}</h3>
            <p className="text-slate-500 font-bold mb-6">{t('how_was_your_tutor', 'How was your tutor?')}</p>

            <div className="flex gap-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} onClick={() => setRating(star)} className="focus:outline-none transition-transform hover:scale-110">
                  <Star size={40} className={star <= rating ? "fill-amber-400 text-amber-400" : "text-slate-200"} />
                </button>
              ))}
            </div>

            <textarea
              value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
              placeholder={t('leave_a_comment', 'Leave a comment (optional)...')}
              className="w-full bg-slate-50 border-2 p-4 rounded-xl outline-none font-bold text-sm resize-none focus:border-indigo-600 transition-all mb-4"
            />

            <button
              onClick={submitRating} disabled={rating === 0 || isSubmittingRating}
              className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingRating ? t('saving', 'Saving...') : t('submit_review', 'Submit Review')}
            </button>
            <button onClick={() => { setShowRatingModal(false); navigate('/student/home'); }} className="mt-4 text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-slate-600">
              {t('skip', 'Skip')}
            </button>
          </div>
        </div>
      )}

      <button onClick={() => navigate('/student/home')} className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-500 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/30 transition-all hover:-translate-y-1 z-[120] group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>

      <DepositModal isOpen={isDepositOpen} onClose={() => setIsDepositOpen(false)} onSuccess={() => setIsDepositOpen(false)} />
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
    </div>
  );
};

export default StudentDashboard;